import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, DatePicker, Drawer, Empty, InputNumber, Segmented, Skeleton, Space, Table, Tooltip, message } from "antd";
import { CaretDownOutlined, CaretRightOutlined, LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  DaySheetScope,
  DaySheetTask,
  EQUIPMENT_HEAD_KEYS,
  EquipmentHeadHours,
  EquipmentHeadKey,
  EquipmentLine,
  EquipmentSaveLine,
  EquipmentSheet,
  normaliseEquipmentSheet,
  siteDailyApis,
  VARIABLE_HEAD_KEYS,
  FixedHeadKey,
  VariableHeadKey,
} from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { fmtQty } from "../executionNorm";
import DaySheetTree from "./DaySheetTree";
import PendingByDateList from "./PendingByDateList";
import CivilTaskFilterBar from "./CivilTaskFilterBar";
import {
  SheetSectionHandle,
  SheetSectionMeta,
  fromInput,
  inputValue,
  isPresent,
  saveSequentially,
  sheetMeta,
  useDaySheet,
  filterCivilSheet,
  perUnitOf,
  perUnitLabel,
  fmtPerUnit,
} from "./daySheet";
import { HeadDraft, mergedHeadOf, todayHeadAmount } from "./mechanicalHeads";
import { splitSavedHours, withExtraDays } from "./mechanicalDays";

// Mechanical: hours worked per machine deployed on each produced task today,
// with the cost heads beneath — three FIXED heads priced from the equipment
// rate card × hours worked (read-only), five CONSUMED heads captured as
// hours × actual ₹/hr — the rate card's own unit, so the report's target and
// actual rates compare. Money is shown here by the
// user's decision; the fuel / meter / breakdown fields stay on the mobile
// form and pass through a web save untouched.

const MAX_HOURS = 24;
// Below this, today's share of a head on a civil date counts as nothing.
const SHARE_EPS = 0.00005;
const VARIABLE_HEAD_LABEL: Record<VariableHeadKey, string> = {
  repair: "Repair",
  misc: "Misc",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
};
const FIXED_HEAD_LABEL: Record<FixedHeadKey, string> = {
  depreciation: "Depreciation",
  interest: "Interest",
  insurance: "Insurance",
};

type HeadDrafts = Record<VariableHeadKey, HeadDraft>;

const HEAD_LABEL: Record<EquipmentHeadKey, string> = {
  depreciation: "Depreciation",
  interest: "Interest",
  repair: "Repair",
  misc: "Misc",
  insurance: "Insurance",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
};

interface LineDraft {
  // Today's own hours (the day-wise entry)...
  hoursWorked: string;
  // ...plus hours entered against earlier civil dates, keyed by work date.
  // Saved as ONE log (hoursWorked + Σ pendingDays) with this split beside it:
  // each part is booked on its civil date exactly (as on Material-Stores).
  pendingDays: Record<string, string>;
  fuelLitres: string;
  readingStart: string;
  readingEnd: string;
  isBreakdown: boolean;
  idleReason: string;
  headHours: EquipmentHeadHours;
  // Consumed heads of the log's own day (not against a civil date)...
  heads: HeadDrafts;
  // ...and those entered against a civil date, keyed by work date: stored on
  // that date and booked there in full (user, 2026-10-14 — never shared by hours).
  dayHeads: Record<string, HeadDrafts>;
}

interface TaskLines {
  loading: boolean;
  error: string;
  sheet: EquipmentSheet | null;
  initial: Record<number, LineDraft>;
  draft: Record<number, LineDraft>;
  saving: boolean;
}

// Consumed heads start from what is stored, else from the head's master
// defaults (unit always; the diesel / electricity price where one exists).
const headDraftsOf = (line: EquipmentLine): HeadDrafts => {
  const drafts = {} as HeadDrafts;
  VARIABLE_HEAD_KEYS.forEach((key) => {
    const h = line.variableHeads.find((x) => x.head === key);
    drafts[key] = {
      qty: h?.qty ?? "",
      uomId: h?.defaultUomId || h?.uomId || 0,
      // No prefill (user rule): the rate is what the clerk types; only a
      // stored rate reads back.
      rate: h?.rateSource === "ENTERED" ? h.rate : "",
    };
  });
  return drafts;
};

const emptyHeads = (line: EquipmentLine): HeadDrafts => {
  const drafts = {} as HeadDrafts;
  VARIABLE_HEAD_KEYS.forEach((key) => {
    const h = line.variableHeads.find((x) => x.head === key);
    drafts[key] = { qty: "", uomId: h?.defaultUomId || h?.uomId || 0, rate: "" };
  });
  return drafts;
};

/** What this log entered against each civil date, per head. */
// Each civil date's heads start from the date's WHOLE booking — every log's
// share folded into one qty and rate (the date owns it, user 2026-10-26).
const dayHeadDraftsOf = (line: EquipmentLine): Record<string, HeadDrafts> => {
  const out: Record<string, HeadDrafts> = {};
  line.civilDays.forEach((day) => {
    if (!day.todayHeads?.length && !day.earlierHeads?.length) return;
    const drafts = emptyHeads(line);
    VARIABLE_HEAD_KEYS.forEach((key) => {
      const merged = mergedHeadOf(day, key);
      if (merged) drafts[key] = { ...drafts[key], qty: merged.qty, rate: merged.rate };
    });
    out[day.workDate] = drafts;
  });
  return out;
};

const headsFilled = (heads: HeadDrafts | undefined) =>
  !!heads && VARIABLE_HEAD_KEYS.some((key) => isPresent(heads[key].qty) || isPresent(heads[key].rate));

// The date-strip key of the "Not against a civil date" group.
const OTHER_TAB = "__other__";
// Past this many dates the rail offers its "Owed" filter.
const RAIL_FILTER_FROM = 8;

/** The machine's own log for the day: today's hours + the log date's own civil part. */
const logHours = (draft: LineDraft, logDate: string): string => {
  const own = draft.pendingDays[logDate] ?? "";
  const days = isPresent(own) ? Number(own) : 0;
  if (days <= 0) return draft.hoursWorked;
  return String(Math.round(((isPresent(draft.hoursWorked) ? Number(draft.hoursWorked) : 0) + days) * 10000) / 10000);
};

/** The same heads on a civil date, box for box. */
const sameHeadsOf = (a: HeadDrafts | undefined, b: HeadDrafts | undefined) =>
  VARIABLE_HEAD_KEYS.every((key) => (a?.[key].qty ?? "") === (b?.[key].qty ?? "") && (a?.[key].rate ?? "") === (b?.[key].rate ?? ""));

const sameDays = (a: Record<string, string>, b: Record<string, string>) => {
  const filled = (days: Record<string, string>) =>
    Object.entries(days)
      .filter(([, v]) => v !== "")
      .sort(([x], [y]) => x.localeCompare(y))
      .join("|");
  return filled(a) === filled(b);
};

const draftOf = (line: EquipmentLine, logDate: string): LineDraft => ({
  ...splitSavedHours(line, logDate),
  fuelLitres: line.fuelLitres,
  readingStart: line.readingStart,
  readingEnd: line.readingEnd,
  isBreakdown: line.isBreakdown,
  idleReason: line.idleReason,
  headHours: { ...line.headHours },
  heads: headDraftsOf(line),
  dayHeads: dayHeadDraftsOf(line),
});

const sameHead = (a: HeadDraft, b: HeadDraft) => a.qty === b.qty && a.uomId === b.uomId && a.rate === b.rate;

// Per-date heads compare on what is filled: an untouched date and one whose
// boxes were typed and cleared again are the same.
const sameDayHeads = (a: Record<string, HeadDrafts>, b: Record<string, HeadDrafts>) => {
  const filled = (days: Record<string, HeadDrafts>) =>
    Object.keys(days)
      .filter((date) => headsFilled(days[date]))
      .sort()
      .map((date) => `${date}:${VARIABLE_HEAD_KEYS.map((key) => `${days[date][key].qty}/${days[date][key].rate}`).join(",")}`)
      .join("|");
  return filled(a) === filled(b);
};

const sameDraft = (a: LineDraft | undefined, b: LineDraft | undefined) =>
  !!a &&
  !!b &&
  a.hoursWorked === b.hoursWorked &&
  sameDays(a.pendingDays, b.pendingDays) &&
  a.fuelLitres === b.fuelLitres &&
  a.readingStart === b.readingStart &&
  a.readingEnd === b.readingEnd &&
  a.isBreakdown === b.isBreakdown &&
  a.idleReason === b.idleReason &&
  EQUIPMENT_HEAD_KEYS.every((key) => a.headHours[key] === b.headHours[key]) &&
  VARIABLE_HEAD_KEYS.every((key) => sameHead(a.heads[key], b.heads[key])) &&
  sameDayHeads(a.dayHeads, b.dayHeads);

const isDirty = (entry: TaskLines | undefined) =>
  !!entry?.sheet && entry.sheet.lines.some((line) => !sameDraft(entry.draft[line.assetId], entry.initial[line.assetId]));

// Blocking problems on one machine's line. The meter note (end before start)
// is deliberately NOT here — it is shown, but does not stop the save.
const lineErrors = (draft: LineDraft | undefined): string[] => {
  if (!draft) return [];
  const errors: string[] = [];
  if (isPresent(draft.hoursWorked) && (Number(draft.hoursWorked) > MAX_HOURS || Number(draft.hoursWorked) < 0)) {
    errors.push(`Hours worked must be 0–${MAX_HOURS}`);
  }
  // 24 h is per day of WORK: each civil date's hours, not the log's total.
  Object.entries(draft.pendingDays).forEach(([workDate, value]) => {
    if (isPresent(value) && (Number(value) > MAX_HOURS || Number(value) < 0)) {
      errors.push(`Hours for ${dayjs(workDate).format("DD-MMM")} must be 0–${MAX_HOURS}`);
    }
  });
  EQUIPMENT_HEAD_KEYS.forEach((key) => {
    const value = draft.headHours[key];
    if (isPresent(value) && (Number(value) > MAX_HOURS || Number(value) < 0)) {
      errors.push(`${HEAD_LABEL[key]} hours must be 0–${MAX_HOURS}`);
    }
  });
  if (draft.isBreakdown && draft.idleReason.trim() === "") {
    errors.push("A breakdown needs a reason");
  }
  const checkHeads = (heads: HeadDrafts, on: string) =>
    VARIABLE_HEAD_KEYS.forEach((key) => {
      const h = heads[key];
      if (isPresent(h.qty) && Number(h.qty) < 0) errors.push(`${VARIABLE_HEAD_LABEL[key]} quantity${on} cannot be negative`);
      if (isPresent(h.rate) && Number(h.rate) < 0) errors.push(`${VARIABLE_HEAD_LABEL[key]} rate${on} cannot be negative`);
    });
  checkHeads(draft.heads, "");
  Object.entries(draft.dayHeads).forEach(([workDate, heads]) => checkHeads(heads, ` for ${dayjs(workDate).format("DD-MMM")}`));
  return errors;
};

// ₹ helpers for the panel — qty × rate, hours × ₹/hr, both '' unless both
// sides are present. Money is formatted with the same 2-dp figure formatter.
const times = (a: string, b: string): number | null =>
  isPresent(a) && isPresent(b) && Number.isFinite(Number(a)) && Number.isFinite(Number(b))
    ? Math.round(Number(a) * Number(b) * 100) / 100
    : null;
const fixedAmount = (hours: string, ratePerHour: string): number | null => times(hours, ratePerHour);
const consumedAmount = todayHeadAmount;
type CivilDay = EquipmentLine["civilDays"][number];
/**
 * Hours the FIXED heads (depreciation, interest, insurance) are charged on:
 * the ACTUAL hours, priced at the rate card's (norm) ₹/hr (user, 2026-10-09).
 * On a civil date, the hours in that date's Actual hrs box — the date's whole
 * booking; without a date, this log's own-day hours.
 */
const fixedHoursOf = (_line: EquipmentLine, draft: LineDraft, day?: CivilDay): string => {
  if (!day) return draft.hoursWorked;
  const hours = draft.pendingDays[day.workDate] ?? "";
  return isPresent(hours) && Number(hours) > 0 ? String(Math.round(Number(hours) * 10000) / 10000) : "";
};
const machineTotal = (
  line: EquipmentLine,
  draft: LineDraft,
  day?: CivilDay
): { fixed: number; consumed: number; total: number } => {
  const fixedHours = fixedHoursOf(line, draft, day);
  const fixed = line.fixedHeads.reduce((sum, h) => sum + (fixedAmount(fixedHours, h.ratePerHour) ?? 0), 0);
  const sumHeads = (heads: HeadDrafts | undefined) =>
    heads ? VARIABLE_HEAD_KEYS.reduce((sum, key) => sum + (consumedAmount(heads[key]) ?? 0), 0) : 0;
  // Under a civil date: that date's whole booking. Without one: every date's
  // booking; the log's own undated heads only when it has no dates at all —
  // once it does, they are already shared into those dates' figures.
  const dated = Object.values(draft.dayHeads);
  const consumed = day
    ? sumHeads(draft.dayHeads[day.workDate])
    : dated.length
      ? dated.reduce((sum, heads) => sum + sumHeads(heads), 0)
      : sumHeads(draft.heads);
  return { fixed, consumed, total: fixed + consumed };
};

const isInvalid = (entry: TaskLines | undefined) =>
  !!entry?.sheet && entry.sheet.lines.some((line) => lineErrors(entry.draft[line.assetId]).length > 0);

interface Props {
  projectId: number;
  date: string; // 'YYYY-MM-DD'
  scope: DaySheetScope;
  phaseId?: number;
  activityId?: number;
  reloadToken: number;
  onSheetChange: (meta: SheetSectionMeta) => void;
  onDirtyChange: (count: number) => void;
}

const MechanicalSection = forwardRef<SheetSectionHandle, Props>(
  ({ projectId, date, scope, phaseId, activityId, reloadToken, onSheetChange, onDirtyChange }, ref) => {
    // Machine hours are booked against civil-completed TASKS, not a day (see
    // Material-Stores): every task with civil done, stamped with today. Tracked
    // tasks with no civil output come too — machines run ahead of the civil
    // work they prepare for, and those hours stay on the day (user, 2026-10-14).
    const { sheet, loading, error, reload } = useDaySheet(
      "equipment",
      projectId,
      { date, scope, phaseId, activityId, civilDone: true, includeTracked: true },
      reloadToken
    );
    const locked = !!sheet?.dayLocked;
    // Civil-done basis: default to tasks still owed machine hours, or every civil-done
    // task for extra / corrective entries; plus a search.
    const [pendingOnly, setPendingOnly] = useState(true);
    // Third view: tracked tasks with no civil output on the date.
    const [noOutputView, setNoOutputView] = useState(false);
    const [taskSearch, setTaskSearch] = useState("");
    // Owed machine hours, or a civil date today's log booked (listed so it can
    // be corrected) — both keep the task in "Pending machine hours".
    const pendingOf = useCallback(
      (task: DaySheetTask) => task.pendingEquipmentCount || (task.pendingEquipmentDates.length > 0 ? 1 : 0),
      []
    );
    // The list also carries tracked tasks that never produced: "All civil-done"
    // keeps to tasks with civil output up to the date, the third view to tasks
    // with none ON the date.
    const civilDoneTask = useCallback((task: DaySheetTask) => Number(task.producedTillDate) > 0, []);
    const noOutputTask = useCallback((task: DaySheetTask) => !(Number(task.producedQty) > 0), []);
    const shownSheet = useMemo(
      () =>
        filterCivilSheet(sheet, {
          pendingOnly,
          search: taskSearch,
          pendingOf,
          match: noOutputView ? noOutputTask : pendingOnly ? undefined : civilDoneTask,
        }),
      [sheet, pendingOnly, noOutputView, taskSearch, pendingOf, civilDoneTask, noOutputTask]
    );
    const civilCounts = useMemo(() => {
      const all = (sheet?.phases ?? []).flatMap((p) => p.activities).flatMap((a) => a.tasks);
      return {
        all: all.filter(civilDoneTask).length,
        pending: all.filter((t) => pendingOf(t) > 0).length,
        noOutput: all.filter(noOutputTask).length,
      };
    }, [sheet, pendingOf, civilDoneTask, noOutputTask]);

    // A task opens in a DRAWER (as on Material-Stores), not inline under its
    // row. The order the drawer walks is frozen when it opens, so a save that
    // drops a task from the pending list never makes "next" skip one.
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    // A stop is a task, or a task ON a civil date when opened from the
    // date-wise Pending list (the drawer then shows that date only).
    const [navOrder, setNavOrder] = useState<Array<{ taskId: number; date: string | null }>>([]);
    const [focusDate, setFocusDate] = useState<string | null>(null);
    const [linesByTask, setLinesByTask] = useState<Record<number, TaskLines>>({});
    const linesRef = useRef(linesByTask);
    linesRef.current = linesByTask;
    // A response for a day the user has since left must never land in the
    // new day's cache (a replace-set save would then copy the old day over).
    const dateRef = useRef(date);
    dateRef.current = date;

    useEffect(() => {
      setLinesByTask({});
      setOpenTaskId(null);
    }, [projectId, date]);

    useEffect(() => {
      onSheetChange(sheetMeta(sheet, loading));
    }, [sheet, loading, onSheetChange]);

    const dirtyIds = useMemo(
      () =>
        Object.entries(linesByTask)
          .filter(([, entry]) => isDirty(entry))
          .map(([taskId]) => Number(taskId)),
      [linesByTask]
    );
    useEffect(() => {
      onDirtyChange(dirtyIds.length);
    }, [dirtyIds, onDirtyChange]);

    const patchTask = useCallback((taskId: number, patch: Partial<TaskLines>) => {
      setLinesByTask((current) => ({
        ...current,
        [taskId]: { ...(current[taskId] || emptyTaskLines()), ...patch },
      }));
    }, []);

    const fetchLines = useCallback(
      async (taskId: number) => {
        const forDate = date;
        patchTask(taskId, { loading: true, error: "" });
        const res = await siteDailyApis.getEquipment(taskId, forDate);
        if (dateRef.current !== forDate) return;
        if (res?.status !== true) {
          patchTask(taskId, { loading: false, error: res?.message || "Could not load equipment lines" });
          return;
        }
        const next = normaliseEquipmentSheet(res.data);
        if (next.logDate && next.logDate !== forDate) return;
        const initial: Record<number, LineDraft> = {};
        next.lines.forEach((line) => {
          initial[line.assetId] = draftOf(line, forDate);
        });
        setLinesByTask((current) => ({
          ...current,
          [taskId]: { loading: false, error: "", sheet: next, initial, draft: { ...initial }, saving: false },
        }));
      },
      [date, patchTask]
    );

    // Lazy: a task's lines load the first time it is opened, and stay (with
    // their draft) while the drawer moves on or closes.
    const showTask = (taskId: number) => {
      setOpenTaskId(taskId);
      const entry = linesRef.current[taskId];
      if (!entry || (entry.error && !entry.loading)) fetchLines(taskId);
    };
    const shownOrder = useMemo(
      () => (shownSheet?.phases || []).flatMap((p) => p.activities.flatMap((a) => a.tasks.map((t) => t.taskId))),
      [shownSheet]
    );
    const tasksById = useMemo(() => {
      const map = new Map<number, DaySheetTask>();
      (sheet?.phases || []).forEach((p) => p.activities.forEach((a) => a.tasks.forEach((t) => map.set(t.taskId, t))));
      return map;
    }, [sheet]);
    const showStop = (stop: { taskId: number; date: string | null }) => {
      setFocusDate(stop.date);
      showTask(stop.taskId);
    };
    const openFromList = (task: DaySheetTask) => {
      setNavOrder(shownOrder.map((taskId) => ({ taskId, date: null })));
      showStop({ taskId: task.taskId, date: null });
    };
    // The date-wise list's order: civil date by civil date, oldest first.
    const dateOrder = useMemo(() => {
      const stops: Array<{ taskId: number; date: string | null }> = [];
      (shownSheet?.phases || []).forEach((p) =>
        p.activities.forEach((a) =>
          a.tasks.forEach((t) => t.pendingEquipmentDates.forEach((d) => stops.push({ taskId: t.taskId, date: d.workDate })))
        )
      );
      return stops.sort((x, y) => (x.date as string).localeCompare(y.date as string));
    }, [shownSheet]);
    const openFromDate = (task: DaySheetTask, workDate: string) => {
      setNavOrder(dateOrder);
      showStop({ taskId: task.taskId, date: workDate });
    };

    const setLineDraft = (taskId: number, assetId: number, patch: Partial<LineDraft>) => {
      setLinesByTask((current) => {
        const entry = current[taskId];
        if (!entry) return current;
        return {
          ...current,
          [taskId]: { ...entry, draft: { ...entry.draft, [assetId]: { ...entry.draft[assetId], ...patch } } },
        };
      });
    };

    const resetTask = (taskId: number) => {
      const entry = linesRef.current[taskId];
      if (!entry) return;
      patchTask(taskId, { draft: { ...entry.initial } });
    };

    const saveTask = useCallback(
      async (taskId: number, quiet = false): Promise<boolean> => {
        const entry = linesRef.current[taskId];
        if (!entry?.sheet || locked || isInvalid(entry)) return false;
        // Every machine goes up as it stands — blank fields stay '' — and the
        // server drops the lines with nothing recorded.
        const lines: EquipmentSaveLine[] = entry.sheet.lines.map((line) => {
          const draft = entry.draft[line.assetId] || draftOf(line, date);
          const initial = entry.initial[line.assetId] || draftOf(line, date);
          // Earlier civil dates go up only when changed: each is the date's
          // WHOLE booking (the date owns it — user, 2026-10-26); the server
          // leaves the dates not sent exactly as they are.
          const dates = new Set([
            ...Object.keys(draft.pendingDays),
            ...Object.keys(draft.dayHeads),
            ...Object.keys(initial.pendingDays),
            ...Object.keys(initial.dayHeads),
          ]);
          const changed = Array.from(dates).filter(
            (workDate) =>
              workDate < date &&
              ((draft.pendingDays[workDate] ?? "") !== (initial.pendingDays[workDate] ?? "") ||
                !sameHeadsOf(draft.dayHeads[workDate], initial.dayHeads[workDate]))
          );
          const headsOn = (heads: HeadDrafts | undefined) =>
            VARIABLE_HEAD_KEYS.map((key) => ({
              head: key,
              qty: heads?.[key].qty ?? "",
              uomId: heads?.[key].uomId ?? 0,
              rate: heads?.[key].rate ?? "",
            }));
          const ownPart = draft.pendingDays[date] ?? "";
          return {
            assetId: line.assetId,
            // ONE log per machine: today's hours plus the log date's own civil
            // part, booked on the day exactly.
            hoursWorked: logHours(draft, date),
            civilDates: [
              ...(isPresent(ownPart) && Number(ownPart) > 0 ? [{ civilDate: date, qty: ownPart }] : []),
              ...changed.map((civilDate) => ({
                civilDate,
                qty: isPresent(draft.pendingDays[civilDate] ?? "") ? draft.pendingDays[civilDate] : "0",
              })),
            ],
            fuelLitres: draft.fuelLitres,
            readingStart: draft.readingStart,
            readingEnd: draft.readingEnd,
            isBreakdown: draft.isBreakdown,
            // Sent as it stands: a reason logged without the breakdown flag
            // (the site app's "idle" case) must survive a save of any other
            // machine on this task-day — the save is a replace-set.
            idleReason: draft.idleReason.trim(),
            headHours: { ...draft.headHours },
            heads: VARIABLE_HEAD_KEYS.map((key) => ({
              head: key,
              qty: draft.heads[key].qty,
              uomId: draft.heads[key].uomId,
              rate: draft.heads[key].rate,
            })),
            // The log date's own heads when filled, and every changed earlier
            // date's heads (all blank = cleared), stored on their dates.
            civilDateHeads: [
              ...(headsFilled(draft.dayHeads[date]) ? [{ civilDate: date, heads: headsOn(draft.dayHeads[date]) }] : []),
              ...changed.map((civilDate) => ({ civilDate, heads: headsOn(draft.dayHeads[civilDate]) })),
            ],
          };
        });
        patchTask(taskId, { saving: true });
        try {
          const res = await siteDailyApis.saveEquipment({ taskId, logDate: date, lines, byCivilDate: true });
          if (res?.status !== true) {
            message.error(res?.message || "Could not save the equipment log");
            patchTask(taskId, { saving: false });
            return false;
          }
          if (!quiet) message.success(res.data?.message || "Equipment log saved");
          if (res.data?.warning) message.warning(res.data.warning, 6);
          await fetchLines(taskId);
          reload();
          return true;
        } catch (err: any) {
          message.error(err?.message || "Could not save the equipment log");
          patchTask(taskId, { saving: false });
          return false;
        }
      },
      [date, locked, patchTask, fetchLines, reload]
    );

    const taskLabel = useCallback(
      (taskId: number) => {
        for (const phase of sheet?.phases || []) {
          for (const activity of phase.activities) {
            const task = activity.tasks.find((t) => t.taskId === taskId);
            if (task) return task.taskSequenceId;
          }
        }
        return `Task ${taskId}`;
      },
      [sheet]
    );

    useImperativeHandle(
      ref,
      () => ({
        discardAll: () => {
          setLinesByTask({});
          setOpenTaskId(null);
        },
        saveAll: async () => {
          // Invalid tasks are left out and named, rather than failing the run.
          const invalid = dirtyIds.filter((id) => isInvalid(linesRef.current[id]));
          const ids = dirtyIds
            .filter((id) => !invalid.includes(id))
            .map((id) => ({ id, label: taskLabel(id) }));
          if (invalid.length) {
            message.warning(`Fix the flagged rows on ${invalid.map(taskLabel).join(", ")} first`, 6);
          }
          if (!ids.length) return;
          await saveSequentially(ids, (id) => saveTask(id, true));
        },
      }),
      [dirtyIds, saveTask, taskLabel]
    );

    // ---- the task drawer ----------------------------------------------------
    const openTask = openTaskId !== null ? tasksById.get(openTaskId) : undefined;
    const openEntry = openTaskId !== null ? linesByTask[openTaskId] : undefined;
    const navIndex =
      openTaskId !== null ? navOrder.findIndex((stop) => stop.taskId === openTaskId && stop.date === focusDate) : -1;
    const prevStop = navIndex > 0 ? navOrder[navIndex - 1] : null;
    const nextStop = navIndex >= 0 && navIndex < navOrder.length - 1 ? navOrder[navIndex + 1] : null;
    const openDirty = isDirty(openEntry);
    const openInvalid = isInvalid(openEntry);

    const saveAndNext = async () => {
      if (openTaskId === null) return;
      // The next task is fixed BEFORE the save — the save may reorder the list.
      const target = nextStop;
      const ok = await saveTask(openTaskId);
      if (ok && target !== null) showStop(target);
    };

    return (
      <>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
        {locked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Day locked — the DPR for ${dayjs(date).format("DD-MMM-YYYY")} has been submitted`}
            description="Equipment logs for this date are read-only."
          />
        )}
        <CivilTaskFilterBar
          pendingOnly={pendingOnly}
          onPendingOnlyChange={(value) => {
            setNoOutputView(false);
            setPendingOnly(value);
          }}
          search={taskSearch}
          onSearchChange={setTaskSearch}
          pendingCount={civilCounts.pending}
          allCount={civilCounts.all}
          what="machine hours"
          noOutput={
            sheet?.scope === "MONITORED"
              ? { active: noOutputView, count: civilCounts.noOutput, onSelect: () => setNoOutputView(true) }
              : undefined
          }
        />
        {pendingOnly && !noOutputView ? (
          // Pending machine hours, DATE-WISE: civil date → activity → task.
          <PendingByDateList
            sheet={shownSheet}
            loading={loading}
            activeTaskId={openTaskId}
            activeDate={focusDate}
            onOpen={openFromDate}
            datesOf={(task) => task.pendingEquipmentDates}
            lineNoun={["machine", "machines"]}
            owedTitle="Machines owed"
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              return entry ? { dirty: isDirty(entry), saving: entry.saving } : undefined;
            }}
            emptyDescription="Nothing owed — every civil date has its machine hours booked"
          />
        ) : (
          <DaySheetTree
            sheet={shownSheet}
            pendingCountOf={pendingOf}
            pendingLabel="hours"
            loading={loading}
            expandedTaskIds={[]}
            onExpandedTaskIdsChange={() => undefined}
            renderTaskDetail={() => null}
            onTaskOpen={openFromList}
            activeTaskId={openTaskId}
            taskState={(task) => {
              const entry = linesByTask[task.taskId];
              if (!entry) return undefined;
              // The task's machinery cost for the day (every machine's fixed +
              // consumed), for the task-level ₹ and ₹ / unit on the tree row.
              const amount = entry.sheet
                ? entry.sheet.lines.reduce(
                    (sum, line) => sum + machineTotal(line, entry.draft[line.assetId] || draftOf(line, date)).total,
                    0
                  )
                : null;
              return { dirty: isDirty(entry), invalid: isInvalid(entry), saving: entry.saving, amount };
            }}
            showTrackedTag={!!sheet?.monitoringConfigured && sheet.scope === "ALL"}
            emptyDescription={
              noOutputView ? (
                <span>
                  Every tracked task produced in Civil today
                  <br />
                  <span style={{ color: "#8094ae", fontSize: 12 }}>
                    This view lists tracked tasks (with equipment norms) that have no civil output on the date.
                  </span>
                </span>
              ) : (
                <span>
                  No tasks produced on this date in Civil
                  <br />
                  <span style={{ color: "#8094ae", fontSize: 12 }}>
                    Enter the day's execution first — this sheet lists the tasks that produced output
                    (with equipment norms; and tracked, when tracking is on).
                  </span>
                </span>
              )
            }
          />
        )}
        <Drawer
          open={openTaskId !== null}
          onClose={() => setOpenTaskId(null)}
          width="min(1180px, 94vw)"
          className="pms-mat-drawer"
          title={
            openTask ? (
              <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                <span>
                  <span className="pms-exec-task-seq">{openTask.taskSequenceId}</span>
                  <span className="pms-exec-task-name">{openTask.taskName}</span>
                </span>
                <span className="pms-exec-hint" style={{ fontWeight: 400 }}>
                  Machinery log · {dayjs(date).format("DD-MMM-YYYY")}
                  {openTask.workUomName && openTask.producedTillDate !== ""
                    ? ` · civil done ${fmtQty(openTask.producedTillDate)} ${openTask.workUomName}`
                    : ""}
                </span>
              </span>
            ) : (
              "Machinery log"
            )
          }
          extra={
            navOrder.length > 1 && navIndex >= 0 ? (
              <Space size={4}>
                <Button size="small" icon={<LeftOutlined />} disabled={prevStop === null} onClick={() => prevStop && showStop(prevStop)} aria-label="Previous task" />
                <span className="pms-exec-hint exec-num">
                  {navIndex + 1} / {navOrder.length}
                </span>
                <Button size="small" icon={<RightOutlined />} disabled={nextStop === null} onClick={() => nextStop && showStop(nextStop)} aria-label="Next task" />
              </Space>
            ) : null
          }
          footer={
            openTaskId === null ? null : (
              <div className="pms-mat-drawer-foot">
                <span className="pms-exec-hint">
                  {locked
                    ? "Day locked — read-only."
                    : openDirty
                      ? "Unsaved changes stay with this task if you move on — save them here or with Save all."
                      : "Blank = not recorded. A machine with nothing recorded is left out of the log."}
                </span>
                {!locked && (
                  <Space>
                    <Button onClick={() => resetTask(openTaskId)} disabled={!openDirty || openEntry?.saving}>
                      Reset
                    </Button>
                    <Tooltip title={openInvalid ? "Fix the flagged rows to save" : ""}>
                      <Button
                        icon={<SaveOutlined />}
                        onClick={() => saveTask(openTaskId)}
                        disabled={!openDirty || openInvalid}
                        loading={openEntry?.saving}
                      >
                        Save
                      </Button>
                    </Tooltip>
                    {nextStop !== null && (
                      <Button
                        type="primary"
                        onClick={openDirty ? saveAndNext : () => showStop(nextStop)}
                        disabled={openDirty && openInvalid}
                        loading={openEntry?.saving}
                      >
                        {openDirty ? "Save & next" : "Next task"} <RightOutlined />
                      </Button>
                    )}
                  </Space>
                )}
              </div>
            )
          }
        >
          {openTask && (
            <EquipmentLinesEditor
              key={`${openTask.taskId}-${focusDate ?? ""}`}
              task={openTask}
              focusDate={focusDate}
              entry={openEntry}
              locked={locked}
              inDrawer
              onChange={(assetId, patch) => setLineDraft(openTask.taskId, assetId, patch)}
              onReset={() => resetTask(openTask.taskId)}
              onSave={() => saveTask(openTask.taskId)}
              onRetry={() => fetchLines(openTask.taskId)}
            />
          )}
        </Drawer>
      </>
    );
  }
);

const emptyTaskLines = (): TaskLines => ({
  loading: false,
  error: "",
  sheet: null,
  initial: {},
  draft: {},
  saving: false,
});

// ------------------------------------------------------------- line editor
const EquipmentLinesEditor: React.FC<{
  task: DaySheetTask;
  entry?: TaskLines;
  locked: boolean;
  onChange: (assetId: number, patch: Partial<LineDraft>) => void;
  onReset: () => void;
  onSave: () => void;
  // In the task drawer the Reset / Save actions live in the drawer footer.
  inDrawer?: boolean;
  onRetry: () => void;
  // Opened from a civil date in the Pending list: that date only.
  focusDate?: string | null;
}> = ({ task, entry, locked, onChange, onReset, onSave, onRetry, inDrawer, focusDate }) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  // Which machines have their cost-head split open — local to this editor.
  const [openHeads, setOpenHeads] = useState<React.Key[]>([]);
  // Civil dates opened / closed by hand; unset = open, or only the focus date
  // open when the drawer came from a date in the Pending list.
  // The civil date the table shows — ONE at a time, picked from the date strip
  // (user, 2026-10-14: the stacked dates made the drawer too long). null =
  // the default: the opened date, else the oldest still owed, else today.
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  // Long rails can narrow to the dates still needing input.
  const [railOwedOnly, setRailOwedOnly] = useState(false);
  // "No civil input" dates opened from the rail's picker beyond those the
  // server sent: an empty no-output day for every machine until saved, when
  // the server returns the date itself. Per task.
  const [extraDates, setExtraDates] = useState<string[]>([]);
  useEffect(() => setExtraDates([]), [task.taskId]);

  if (!entry || (entry.loading && !entry.sheet)) {
    return <Skeleton active title={false} paragraph={{ rows: 3 }} />;
  }
  if (entry.error && !entry.sheet) {
    return (
      <Alert
        type="error"
        showIcon
        message={entry.error}
        action={
          <Button size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }
  const sheet = entry.sheet as EquipmentSheet;
  if (!sheet.lines.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No equipment norms on this task" />;
  }

  const dirty = isDirty(entry);
  const invalid = isInvalid(entry);
  const disabled = locked || entry.saving;
  const draftFor = (line: EquipmentLine) => entry.draft[line.assetId] || draftOf(line, sheet.logDate);

  // ONE view (user, 2026-10-09): hours by CIVIL DATE — each date's machines
  // with Target hrs, the Actual hrs box and the machine's cost heads. Fuel,
  // meter readings and breakdown stay on the mobile form and the wire.
  // ₹ per unit of today's output (Cum on excavation tasks) for every cost figure.
  const unit = perUnitLabel(sheet.workUomName);
  const pu = (amount: number | null | undefined): number | null => perUnitOf(amount ?? null, sheet.producedQty);

  // Task level: every machine's day total, and that per unit of output.
  const taskTotal = sheet.lines.reduce((sum, line) => sum + machineTotal(line, draftFor(line)).total, 0);
  const taskPerUnit = taskTotal > 0 ? pu(taskTotal) : null;

  // The consumed heads a row edits: under a civil date, that date's own (stored
  // on the date, user 2026-10-14); otherwise the log's own day.
  const headsOf = (line: EquipmentLine, day?: CivilDay): HeadDrafts => {
    const draft = draftFor(line);
    return day ? draft.dayHeads[day.workDate] ?? emptyHeads(line) : draft.heads;
  };

  // The cost-head panel toggle of a row: the machine's ₹ for the row and how
  // many consumed heads are filled. Under a civil date the heads are THAT
  // date's, so each date's panel holds its own fuel, repair, …
  const headsToggle = (line: EquipmentLine, rowKey: React.Key, day?: CivilDay) => {
    const draft = draftFor(line);
    const rowHeads = headsOf(line, day);
    const consumedSet = VARIABLE_HEAD_KEYS.filter((key) => isPresent(rowHeads[key].qty)).length;
    const { total } = machineTotal(line, draft, day);
    const open = openHeads.includes(rowKey);
    return (
      <Button
        type="link"
        size="small"
        style={{ padding: 0 }}
        onClick={() => setOpenHeads((current) => (open ? current.filter((key) => key !== rowKey) : [...current, rowKey]))}
      >
        {open ? <CaretDownOutlined /> : <CaretRightOutlined />}{" "}
        {/* One figure: the date's whole cost for the machine — fixed and
            operational, whichever log entered it. */}
        {total > 0 ? (
          <span className="exec-num">₹ {fmtQty(String(Math.round(total * 100) / 100))}</span>
        ) : (
          "Cost heads"
        )}
        {consumedSet ? <span className="pms-exec-hint" style={{ marginLeft: 6 }}>{consumedSet} of 5 consumed</span> : null}
      </Button>
    );
  };

  const renderHeads = (line: EquipmentLine, day?: CivilDay) => {
    const draft = draftFor(line);
    const totals = machineTotal(line, draft, day);
    const fixedHours = fixedHoursOf(line, draft, day);
    const hoursKnown = isPresent(fixedHours);
    // The civil date's own DPR governs its boxes (user, 2026-10-26).
    const boxDisabled = disabled || !!day?.dayLocked;
    // ₹ per unit of THAT civil date's output when opened under a date.
    const puDay = (amount: number | null | undefined): number | null =>
      day ? perUnitOf(amount ?? null, day.producedQty) : pu(amount);
    const noteDay = (amount: number) => {
      const v = amount > 0 ? puDay(amount) : null;
      return v === null ? null : (
        <span className="pms-exec-per-unit">
          ₹ {fmtPerUnit(v)} / {unit}
        </span>
      );
    };
    const noRate = line.rateStatus === "NO_RATE";
    const rowHeads = headsOf(line, day);
    const setHead = (key: VariableHeadKey, patch: Partial<HeadDraft>) => {
      const next = { ...rowHeads, [key]: { ...rowHeads[key], ...patch } };
      if (day) onChange(line.assetId, { dayHeads: { ...draft.dayHeads, [day.workDate]: next } });
      else onChange(line.assetId, { heads: next });
    };

    const fixedRows = line.fixedHeads.map((h) => ({
      key: h.head,
      label: FIXED_HEAD_LABEL[h.head],
      hours: fixedHours,
      ratePerHour: h.ratePerHour,
      amount: fixedAmount(fixedHours, h.ratePerHour),
    }));
    const consumedRows = VARIABLE_HEAD_KEYS.map((key) => {
      const h = rowHeads[key];
      const meta = line.variableHeads.find((x) => x.head === key);
      return {
        key,
        label: VARIABLE_HEAD_LABEL[key],
        draft: h,
        amount: consumedAmount(h),
        uomName: meta?.defaultUomName || meta?.uomName || "",
        defaultUomName: meta?.defaultUomName || "",
        normRate: meta?.normRatePerHour ?? "",
        // Which OTHER logs booked this head on the date — a hint only; the
        // boxes hold the date's whole booking.
        from: mergedHeadOf(day, key)?.from ?? "",
      };
    });
    // The hours the TARGET of a consumed head is read on: the hours entered for
    // this civil date (earlier logs + this log's share), else this log's own
    // hours. Target ₹ = those hours x the rate card's ₹/hr for the head.
    const targetHours = (() => {
      if (day) {
        const hours = draft.pendingDays[day.workDate] ?? "";
        return isPresent(hours) && Number(hours) > 0 ? Number(hours) : null;
      }
      return isPresent(draft.hoursWorked) ? Number(draft.hoursWorked) : null;
    })();

    return (
      <div className="pms-exec-heads">
        <div className="pms-exec-heads-block">
          <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
            <span>
              <span className="pms-eyebrow">Owning cost (Fixed heads)</span>
              <div className="pms-exec-hint">
                Actual hours{day ? ` on ${dayjs(day.workDate).format("DD-MMM")}` : ""} × the rate card's norm ₹/hr — derived, not entered
                {noRate ? " · this machine has no rate card on this date" : ""}
              </div>
            </span>
            <span className="pms-exec-heads-figure">
              <span className="exec-num pms-exec-heads-subtotal">₹ {fmtQty(totals.fixed)}</span>
              {noteDay(totals.fixed)}
            </span>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            className="pms-exec-heads-table pms-exec-heads-table--fixed"
            dataSource={fixedRows}
            columns={[
              { title: "Head", dataIndex: "label", key: "label", width: 160 },
              {
                title: "Hours",
                key: "hours",
                width: 110,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ color: hoursKnown ? "#364a63" : "#8094ae" }}>
                    {hoursKnown ? fmtQty(row.hours) : "—"}
                  </span>
                ),
              },
              {
                title: "Rate ₹/hr",
                key: "rate",
                width: 130,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ color: row.ratePerHour === "" ? "#8094ae" : "#364a63" }}>
                    {row.ratePerHour === "" ? "—" : fmtQty(row.ratePerHour)}
                  </span>
                ),
              },
              {
                title: "Amount ₹",
                key: "amount",
                width: 140,
                align: "right" as const,
                render: (_: any, row: any) => (
                  <span className="exec-num" style={{ fontWeight: row.amount === null ? 400 : 600, color: row.amount === null ? "#8094ae" : "#364a63" }}>
                    {row.amount === null ? "—" : fmtQty(row.amount)}
                  </span>
                ),
              },
              {
                title: `₹ / ${unit}`,
                key: "perUnit",
                width: 120,
                align: "right" as const,
                render: (_: any, row: any) => {
                  const v = row.amount === null ? null : puDay(row.amount);
                  return (
                    <span className="exec-num" style={{ color: v === null ? "#8094ae" : "#526484" }}>
                      {v === null ? "—" : fmtPerUnit(v)}
                    </span>
                  );
                },
              },
            ]}
          />
        </div>

        <div className="pms-exec-heads-block">
          <div className="pms-exec-lines-head" style={{ marginBottom: 6 }}>
            <span>
              <span className="pms-eyebrow">Operational cost</span>
              <div className="pms-exec-hint">
                Qty × actual rate · one figure for the date, whoever logged it · target = hours entered
                {targetHours !== null ? ` (${fmtQty(String(Math.round(targetHours * 100) / 100))} h)` : ""} × the rate card's ₹/hr
              </div>
            </span>
            <span className="pms-exec-heads-figure">
              <span className="exec-num pms-exec-heads-subtotal">₹ {fmtQty(String(Math.round((totals.consumed) * 100) / 100))}</span>
              {noteDay(totals.consumed)}
            </span>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            className="pms-exec-heads-table"
            dataSource={consumedRows}
            columns={[
              { title: "Head", dataIndex: "label", key: "label", width: 160 },
              {
                title: "Qty",
                key: "qty",
                width: 170,
                render: (_: any, row: any) => {
                  const rate = isPresent(row.normRate) ? Number(row.normRate) : null;
                  const target = rate !== null && targetHours !== null ? Math.round(rate * targetHours * 100) / 100 : null;
                  return (
                    <div>
                      <Tooltip title={row.from ? `includes the ${row.from}` : ""}>
                        <InputNumber
                          stringMode
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={inputValue(row.draft.qty)}
                          // The date's whole booking of the head, whichever log
                          // entered it; what is typed replaces it (mechanicalHeads).
                          onChange={(v) => setHead(row.key, { qty: fromInput(v) })}
                          disabled={boxDisabled}
                          style={{ width: "100%" }}
                        />
                      </Tooltip>
                      {/* The norm for the hours entered — what this head should cost. */}
                      {rate === null ? (
                        <div className="pms-exec-hint">no rate card</div>
                      ) : target === null ? (
                        <div className="pms-exec-hint">norm ₹ {fmtQty(row.normRate)} / hr — enter hours</div>
                      ) : (
                        // The working (hours × norm ₹/hr) on hover, the figure inline.
                        <Tooltip
                          title={`${fmtQty(String(Math.round((targetHours as number) * 100) / 100))} h × ₹ ${fmtQty(row.normRate)}/hr`}
                        >
                          <div className="pms-exec-hint">target ₹ {fmtQty(String(target))}</div>
                        </Tooltip>
                      )}
                    </div>
                  );
                },
              },
              {
                title: "UOM",
                key: "uom",
                width: 130,
                render: (_: any, row: any) => (
                  <Tooltip title="Fixed for this head — from the UOM master">
                    <span className="pms-exec-fixed-uom">{row.uomName || "—"}</span>
                  </Tooltip>
                ),
              },
              {
                title: "Actual rate ₹ / unit",
                key: "rate",
                width: 170,
                render: (_: any, row: any) => {
                  const qty = isPresent(row.draft.qty) ? Number(row.draft.qty) : 0;
                  // One rate for the date's whole booking, always editable until
                  // the date is submitted (the civil date owns it — user, 2026-10-26).
                  const unpriced = qty > SHARE_EPS && !isPresent(row.draft.rate);
                  return (
                    <div>
                      <InputNumber
                        stringMode
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        status={unpriced ? "warning" : undefined}
                        value={inputValue(row.draft.rate)}
                        onChange={(v) => setHead(row.key, { rate: fromInput(v) })}
                        disabled={boxDisabled}
                        style={{ width: "100%" }}
                      />
                      {unpriced ? <div className="pms-exec-hint" style={{ color: "#faad14" }}>enter the rate — the quantity is unpriced</div> : null}
                    </div>
                  );
                },
              },
              {
                title: "Actual ₹",
                key: "amount",
                width: 140,
                align: "right" as const,
                // The date's whole running cost for the head: earlier logs'
                // share plus today's qty x rate.
                render: (_: any, row: any) => {
                  const amount = todayHeadAmount(row.draft);
                  return (
                    <span className="exec-num" style={{ fontWeight: amount === null ? 400 : 600, color: amount === null ? "#8094ae" : "#364a63" }}>
                      {amount === null ? "—" : fmtQty(String(Math.round(amount * 100) / 100))}
                    </span>
                  );
                },
              },
              {
                title: `₹ / ${unit}`,
                key: "perUnit",
                width: 120,
                align: "right" as const,
                render: (_: any, row: any) => {
                  const amount = todayHeadAmount(row.draft);
                  const v = amount === null ? null : puDay(amount);
                  return (
                    <span className="exec-num" style={{ color: v === null ? "#8094ae" : "#526484" }}>
                      {v === null ? "—" : fmtPerUnit(v)}
                    </span>
                  );
                },
              },
            ]}
          />
        </div>

        <div className="pms-exec-heads-total">
          <span className="pms-exec-hint">
            {day ? `Machine total for ${dayjs(day.workDate).format("DD-MMM")} · fixed + operational` : "Machine total for the day · fixed + operational"}
          </span>
          <span className="pms-exec-heads-figure">
            <span className="exec-num" style={{ fontWeight: 600, fontSize: 15 }}>
              ₹ {fmtQty(String(Math.round((totals.total) * 100) / 100))}
            </span>
            {noteDay(totals.total)}
          </span>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------- hours by civil date
  // Each civil date with the machines it needs: Target hrs (norm per unit x the
  // day's output) and the Actual hrs box — the date's whole booking, whichever
  // log entered it. What is typed is saved as the date's booking (the server
  // takes it over from other logs): the civil date owns it (user, 2026-10-26).
  const EPS = 0.00005;
  const num = (v: string) => (isPresent(v) ? Number(v) : 0);
  const q2 = (n: number) => String(Math.round(n * 10000) / 10000);
  // The log date on a task with no civil output is always live: machines run
  // ahead of the work, and what is typed there stays on the day.
  // Every date with a booking is live too: it can be corrected from here
  // whichever log entered it.
  const liveDay = (d: EquipmentLine["civilDays"][number]) =>
    !!d.noOutput || num(d.pendingQty) > 0 || num(d.bookedQty) > 0;
  const roomOf = (d: EquipmentLine["civilDays"][number]) => Math.max(0, num(d.requiredQty));
  type DateRow =
    | { kind: "day"; key: string; workDate: string; open: boolean; producedQty: string; count: number; owed: number; noOutput: boolean }
    | { kind: "machine"; key: string; line: EquipmentLine; day: EquipmentLine["civilDays"][number] }
    // Today's log that is NOT against a civil date (hours or cost heads typed
    // on the day itself, mobile, Excel) — kept visible and editable.
    | { kind: "otherHead"; key: string; count: number }
    | { kind: "other"; key: string; line: EquipmentLine };
  type DayTab = { key: string; workDate: string; owed: number; entered: number; noOutput: boolean; producedQty: string; count: number };
  const dateRows: DateRow[] = [];
  const dayTabs: DayTab[] = [];
  let selectedTab: string | null = null;
  {
    const byDate = new Map<
      string,
      { producedQty: string; noOutput: boolean; items: Array<{ line: EquipmentLine; day: EquipmentLine["civilDays"][number] }> }
    >();
    // Every date still owed or booked today; the clicked date always (booked
    // or not), FIRST and open, the others collapsed beneath it (user, 2026-10-09).
    sheet.lines.forEach((line) =>
      withExtraDays(line.civilDays, extraDates)
        .filter((d) => liveDay(d) || (focusDate !== null && focusDate !== undefined && d.workDate === focusDate && num(d.requiredQty) > 0))
        .forEach((d) => {
          const slot = byDate.get(d.workDate) ?? { producedQty: d.producedQty, noOutput: !!d.noOutput, items: [] };
          slot.items.push({ line, day: d });
          byDate.set(d.workDate, slot);
        })
    );
    // The date rail, oldest first (today's no-output day falls last). Only
    // the picked date's machines are listed.
    const dayKeys = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
    dayKeys.forEach((workDate) => {
      const slot = byDate.get(workDate)!;
      const owed = slot.items.filter(({ line, day }) => num(draftFor(line).pendingDays[day.workDate] ?? "") + EPS < roomOf(day)).length;
      const entered = slot.items.filter(
        ({ line }) => isPresent(draftFor(line).pendingDays[workDate] ?? "") || headsFilled(draftFor(line).dayHeads[workDate])
      ).length;
      dayTabs.push({ key: workDate, workDate, owed, entered, noOutput: slot.noOutput, producedQty: slot.producedQty, count: slot.items.length });
    });
    const listed = new Set<number>();
    byDate.forEach((slot) => slot.items.forEach(({ line }) => listed.add(line.assetId)));
    const others = sheet.lines.filter((line) => {
      const d = draftFor(line);
      const ownHeads = VARIABLE_HEAD_KEYS.some((key) => isPresent(d.heads[key].qty) || isPresent(d.heads[key].rate));
      // A machine under civil dates still shows here while its log carries
      // heads of its own day (entered before heads went per date): they are
      // shared over its dates by hours until moved onto the dates.
      if (listed.has(line.assetId)) return ownHeads;
      return isPresent(d.hoursWorked) || ownHeads;
    });
    if (others.length) dayTabs.push({ key: OTHER_TAB, workDate: "", owed: 0, entered: others.length, noOutput: false, producedQty: "", count: others.length });

    const has = (key: string | null | undefined) => !!key && dayTabs.some((tab) => tab.key === key);
    const fallback =
      (has(focusDate) ? focusDate : null) ??
      dayTabs.find((tab) => tab.key !== OTHER_TAB && !tab.noOutput && tab.owed > 0)?.key ??
      // Among no-civil-input days, the log date itself before earlier ones.
      dayTabs.find((tab) => tab.noOutput && tab.workDate === sheet.logDate)?.key ??
      dayTabs.find((tab) => tab.noOutput)?.key ??
      dayTabs[0]?.key ??
      null;
    selectedTab = has(pickedDate) ? pickedDate : fallback;

    if (selectedTab === OTHER_TAB) {
      dateRows.push({ kind: "otherHead", key: "OTHER", count: others.length });
      others.forEach((line) => dateRows.push({ kind: "other", key: `O-${line.assetId}`, line }));
    } else if (selectedTab) {
      const tab = dayTabs.find((t) => t.key === selectedTab)!;
      const slot = byDate.get(selectedTab)!;
      dateRows.push({
        kind: "day",
        key: `D-${selectedTab}`,
        workDate: selectedTab,
        open: true,
        producedQty: slot.producedQty,
        count: slot.items.length,
        owed: tab.owed,
        noOutput: slot.noOutput,
      });
      slot.items.forEach(({ line, day }) => dateRows.push({ kind: "machine", key: `${selectedTab}-${line.assetId}`, line, day }));
    }
  }
  const setDayHours = (line: EquipmentLine, workDate: string, hours: string) =>
    onChange(line.assetId, { pendingDays: { ...draftFor(line).pendingDays, [workDate]: hours } });
  const dateSpan = (row: DateRow, key: string) =>
    row.kind !== "day" && row.kind !== "otherHead"
      ? {}
      : key === "machine"
        ? { className: "pms-mat-group-cell" }
        : key === "target"
          ? { colSpan: 3, className: "pms-mat-group-cell" }
          : { colSpan: 0 };
  const dateColumns = [
    {
      title: "Civil date / Machine",
      key: "machine",
      width: w(340),
      onCell: (row: DateRow) => dateSpan(row, "machine"),
      render: (_: any, row: DateRow) =>
        row.kind === "otherHead" ? (
          <span className="pms-mat-group-name">
            <span style={{ color: "#022c57", fontWeight: 600 }}>Not against a civil date</span>
            <span className="pms-exec-group-count">today's log</span>
          </span>
        ) : row.kind === "day" ? (
          <span className="pms-mat-group-name">
            <span style={{ color: "#022c57", fontWeight: 600 }}>{dayjs(row.workDate).format("DD-MMM-YYYY")}</span>
            {row.workDate === focusDate && <span className="pms-exec-count-badge">opened</span>}
            {dateRows.some((r) => r.kind === "machine" && r.day.workDate === row.workDate && r.day.dayLocked) && (
              <span className="pms-exec-count-badge">submitted — locked</span>
            )}
            {row.noOutput ? (
              <span className="pms-exec-group-count">no civil input — machine hours stay on this day, no target</span>
            ) : row.producedQty !== "" && (
              <span className="pms-exec-group-count">
                produced {fmtQty(row.producedQty)} {sheet.workUomName}
              </span>
            )}
          </span>
        ) : (
          <div style={{ paddingLeft: 18 }}>
            <div style={{ color: "#022c57", fontWeight: 600 }}>{row.line.assetName}</div>
            <div className="pms-exec-hint">{row.line.assetCode}</div>
          </div>
        ),
    },
    {
      title: "Target hrs",
      key: "target",
      width: w(140),
      align: "right" as const,
      onCell: (row: DateRow) => dateSpan(row, "target"),
      render: (_: any, row: DateRow) => {
        if (row.kind === "otherHead") {
          return (
            <span className="pms-exec-hint" style={{ display: "block", textAlign: "right" }}>
              {row.count} machine{row.count === 1 ? "" : "s"} · hours book on their own day, else oldest civil date first ·
              cost heads here are shared over the machine's dates by hours — enter them under a date to book them there
            </span>
          );
        }
        if (row.kind === "other") return <span style={{ color: "#8094ae" }}>—</span>;
        if (row.kind === "day" && row.noOutput) {
          return (
            <span className="pms-exec-hint" style={{ display: "block", textAlign: "right" }}>
              {row.count} machine{row.count === 1 ? "" : "s"} · no target
            </span>
          );
        }
        if (row.kind === "day") {
          const owing = dateRows.filter(
            (r): r is Extract<DateRow, { kind: "machine" }> =>
              r.kind === "machine" &&
              r.day.workDate === row.workDate &&
              !r.day.dayLocked &&
              num(draftFor(r.line).pendingDays[row.workDate] ?? "") + EPS < roomOf(r.day)
          );
          return (
            <span className="pms-mat-day-actions" style={{ justifyContent: "flex-end", width: "100%" }}>
              <span className="pms-exec-hint">
                {row.count} machine{row.count === 1 ? "" : "s"} · {row.owed} owed
              </span>
              {!disabled && owing.length > 0 && (
                <Button size="small" onClick={() => owing.forEach((r) => setDayHours(r.line, row.workDate, q2(roomOf(r.day))))}>
                  Fill this date
                </Button>
              )}
            </span>
          );
        }
        if (row.day.noOutput) return <span style={{ color: "#8094ae" }}>—</span>;
        return (
          <span className="exec-num" style={{ color: "#526484" }}>
            {fmtQty(row.day.requiredQty)} h
          </span>
        );
      },
    },
    {
      title: "Actual hrs",
      key: "actual",
      width: w(200),
      onCell: (row: DateRow) => dateSpan(row, "actual"),
      render: (_: any, row: DateRow) => {
        if (row.kind === "other") {
          const draft = draftFor(row.line);
          return (
            <InputNumber
              stringMode
              min="0"
              step="0.5"
              placeholder="0.0"
              value={inputValue(draft.hoursWorked)}
              onChange={(v) => onChange(row.line.assetId, { hoursWorked: fromInput(v) })}
              disabled={disabled}
            />
          );
        }
        if (row.kind !== "machine") return null;
        const { line, day } = row;
        const shown = draftFor(line).pendingDays[day.workDate] ?? "";
        const required = num(day.requiredQty);
        const room = roomOf(day);
        // The civil date's own DPR governs its box (user, 2026-10-26).
        const fixed = !!day.dayLocked;
        return (
          <div>
            <Tooltip
              title={
                fixed ? "Day submitted — locked" : ""
              }
            >
              <InputNumber
                stringMode
                min="0"
                step="0.5"
                placeholder="0.0"
                value={inputValue(shown)}
                onChange={(v) => setDayHours(line, day.workDate, fromInput(v))}
                disabled={disabled || fixed}
              />
            </Tooltip>
            {!fixed && room > EPS && num(shown) + EPS < room && !disabled && (
              <div className="pms-exec-hint">
                <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => setDayHours(line, day.workDate, q2(room))}>
                  use {fmtQty(q2(required))} h
                </Button>
              </div>
            )}
            {!day.noOutput && num(shown) > required + EPS && (
              <div className="pms-exec-hint pms-exec-hint--amber">over target by {fmtQty(q2(num(shown) - required))} h</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Cost heads",
      key: "heads",
      width: w(220),
      onCell: (row: DateRow) => dateSpan(row, "heads"),
      render: (_: any, row: DateRow) =>
        row.kind === "machine" ? headsToggle(row.line, row.key, row.day) : row.kind === "other" ? headsToggle(row.line, row.key) : null,
    },
  ];

  const problems = sheet.lines.flatMap((line) =>
    lineErrors(entry.draft[line.assetId]).map((text) => `${line.assetName}: ${text}`)
  );

  // The rail: all dates, or (once long) only those still needing input — owed
  // or the no-output day — plus whatever is selected, so it never vanishes.
  // "Owed": dates still owed hours, today, and any date with entries — a
  // no-civil-input day with nothing typed is only a calendar slot.
  const railNeeds = (tab: DayTab) =>
    tab.owed > 0 || tab.entered > 0 || tab.key === OTHER_TAB || (tab.noOutput && tab.workDate === sheet.logDate);
  const railTabs =
    railOwedOnly && dayTabs.length > RAIL_FILTER_FROM
      ? dayTabs.filter((tab) => railNeeds(tab) || tab.key === selectedTab)
      : dayTabs;
  const selectedIndex = dayTabs.findIndex((tab) => tab.key === selectedTab);

  // One more "no civil input" date: any day on or before the log date not in
  // the rail yet. It opens as an empty day for every machine; saving pins what
  // is typed there to that date (no target, stays on the day).
  const listedDates = new Set(dayTabs.filter((tab) => tab.key !== OTHER_TAB).map((tab) => tab.workDate));
  const addDatePicker = locked ? null : (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span className="pms-exec-hint">Machines on a date with no civil input:</span>
      <DatePicker
        size="small"
        placeholder="Add a date"
        value={null}
        format="DD-MMM-YYYY"
        allowClear={false}
        disabledDate={(d: Dayjs) => d.format("YYYY-MM-DD") > sheet.logDate || listedDates.has(d.format("YYYY-MM-DD"))}
        onChange={(d: Dayjs | null) => {
          if (!d) return;
          const date = d.format("YYYY-MM-DD");
          setExtraDates((current) => (current.includes(date) ? current : [...current, date]));
          setPickedDate(date);
        }}
      />
    </span>
  );
  const dateTable = (
    <Table
      size="small"
      rowKey="key"
      className="pms-exec-heads-table pms-mat-table"
      dataSource={dateRows}
      columns={dateColumns as any}
      pagination={false}
      rowClassName={(row: DateRow) => (row.kind === "day" || row.kind === "otherHead" ? "pms-mat-group-row" : "")}
      expandable={{
        showExpandColumn: false,
        expandedRowKeys: openHeads,
        rowExpandable: (row: DateRow) => row.kind === "machine" || row.kind === "other",
        expandedRowRender: (row: DateRow) =>
          row.kind === "machine" ? renderHeads(row.line, row.day) : row.kind === "other" ? renderHeads(row.line) : null,
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={focusDate ? `No machine hours owed for ${dayjs(focusDate).format("DD-MMM-YYYY")}` : "No machine hours owed to earlier civil work"}
          />
        ),
      }}
    />
  );

  return (
    <div className="pms-exec-lines">
      <div className="pms-exec-lines-head">
        <span className="pms-exec-hint">
          {sheet.lines.length} machine{sheet.lines.length === 1 ? "" : "s"} ·{" "}
          {sheet.producedTillDate === "" ? (
            <span className="pms-exec-hint--amber">no civil work on this task yet</span>
          ) : (
            <>
              civil done till date <span className="exec-num">{fmtQty(sheet.producedTillDate)}</span> {sheet.workUomName}
            </>
          )}
          {sheet.hasEntryForDate ? " · log recorded" : " · no log recorded yet"}
          {taskTotal > 0 && (
            <>
              {" · machinery today "}
              <span className="exec-num" style={{ color: "#364a63", fontWeight: 600 }}>₹ {fmtQty(taskTotal)}</span>
              {taskPerUnit !== null && (
                <>
                  {" · "}
                  <span className="exec-num" style={{ color: "#364a63", fontWeight: 600 }}>
                    ₹ {fmtPerUnit(taskPerUnit)} / {unit}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </div>
      {dayTabs.length > 1 ? (
        // Master–detail: a scrollable rail of civil dates (any number — grouped
        // by month, an owed-only filter once long) beside ONE date's machines,
        // with previous / next to step through (user, 2026-10-14).
        <div className="pms-exec-date-layout" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div
            className="pms-exec-date-rail"
            style={{ width: w(210), flex: "none", border: "1px solid #e5e9f2", borderRadius: 6, background: "#fff" }}
          >
            <div style={{ padding: "8px 10px", borderBottom: "1px solid #e5e9f2" }}>
              <div style={{ fontWeight: 600, color: "#022c57" }}>
                Dates <span className="pms-exec-hint">({dayTabs.length})</span>
              </div>
              {dayTabs.length > RAIL_FILTER_FROM && (
                <Segmented
                  size="small"
                  block
                  style={{ marginTop: 6 }}
                  value={railOwedOnly ? "owed" : "all"}
                  onChange={(v) => setRailOwedOnly(v === "owed")}
                  options={[
                    { label: `Owed (${dayTabs.filter(railNeeds).length})`, value: "owed" },
                    { label: "All", value: "all" },
                  ]}
                />
              )}
            </div>
            <div style={{ maxHeight: "calc(100vh - 330px)", minHeight: 120, overflowY: "auto", padding: "4px 0" }}>
              {railTabs.map((tab, index) => {
                const month = tab.key === OTHER_TAB ? "" : dayjs(tab.workDate).format("MMM YYYY");
                const before = railTabs[index - 1];
                const prevMonth = !before || before.key === OTHER_TAB ? "" : dayjs(before.workDate).format("MMM YYYY");
                const active = tab.key === selectedTab;
                const status =
                  tab.key === OTHER_TAB
                    ? { text: `${tab.count} machine${tab.count === 1 ? "" : "s"}`, color: "#8094ae" }
                    : tab.noOutput
                      ? { text: "no civil input", color: "#1c6dd0" }
                      : tab.owed > 0
                        ? { text: `${tab.owed} owed`, color: "#c47a00" }
                        : { text: "booked", color: "#1e8e3e" };
                return (
                  <React.Fragment key={tab.key}>
                    {month && month !== prevMonth && (
                      <div className="pms-eyebrow" style={{ padding: "6px 10px 2px", fontSize: 11 }}>
                        {month}
                      </div>
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setPickedDate(tab.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setPickedDate(tab.key);
                      }}
                      style={{
                        cursor: "pointer",
                        padding: "5px 10px",
                        borderLeft: `3px solid ${active ? "#1c6dd0" : "transparent"}`,
                        background: active ? "#eef4fd" : undefined,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#022c57", whiteSpace: "nowrap" }}>
                        {tab.key === OTHER_TAB ? "No civil date" : dayjs(tab.workDate).format("DD ddd")}
                      </span>
                      <span style={{ fontSize: 12, color: status.color, textAlign: "right" }}>
                        {status.text}
                        {tab.entered > 0 && tab.key !== OTHER_TAB && <span style={{ color: "#526484" }}> · {tab.entered} entered</span>}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
              {!railTabs.length && (
                <div className="pms-exec-hint" style={{ padding: "8px 10px" }}>
                  Nothing owed — switch to All
                </div>
              )}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
              {addDatePicker ?? <span />}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span className="pms-exec-hint">
                date {selectedIndex + 1} of {dayTabs.length}
              </span>
              <Button
                size="small"
                icon={<LeftOutlined />}
                disabled={selectedIndex <= 0}
                onClick={() => setPickedDate(dayTabs[selectedIndex - 1].key)}
              />
              <Button
                size="small"
                icon={<RightOutlined />}
                disabled={selectedIndex < 0 || selectedIndex >= dayTabs.length - 1}
                onClick={() => setPickedDate(dayTabs[selectedIndex + 1].key)}
              />
              </span>
            </div>
            {dateTable}
          </div>
        </div>
      ) : (
        <>
          {addDatePicker && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>{addDatePicker}</div>}
          {dateTable}
        </>
      )}
      {problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 10 }}
          message={problems.length === 1 ? problems[0] : `${problems.length} rows need attention`}
          description={
            problems.length > 1 ? (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            ) : undefined
          }
        />
      )}
      {!inDrawer && (
      <div className="pms-exec-lines-foot">
        <span className="pms-exec-hint">
          Blank = not recorded. A machine with nothing recorded is left out of the log.
        </span>
        {!locked && (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button size="small" onClick={onReset} disabled={!dirty || entry.saving}>
              Reset
            </Button>
            <Tooltip title={invalid ? "Fix the flagged rows to save" : ""}>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSave}
                disabled={!dirty || invalid}
                loading={entry.saving}
              >
                Save {task.taskSequenceId}
              </Button>
            </Tooltip>
          </span>
        )}
      </div>
      )}
    </div>
  );
};

export default MechanicalSection;
